/**
 * BrandIcon - logos officiels Microsoft (M365, Azure) en SVG inline.
 * v2026.06.120 - pour les tabs Compliance Manager.
 *
 * Pourquoi du SVG inline plutot que des PNG/img tags :
 *  - rendu net sur tous les DPR (Retina, 4K)
 *  - pas de requete HTTP supplementaire
 *  - cross-browser y compris Edge sur Windows (le bug emoji deja documente)
 */
import type { CSSProperties } from 'react';

interface BrandIconProps {
  /** Logo a afficher */
  name: 'm365' | 'azure';
  /** Taille en px. Defaut 16 (pour tab). */
  size?: number;
  /** Inline style (e.g. marginRight) */
  style?: CSSProperties;
  /** ARIA label - defaut humain par logo. */
  label?: string;
}

/**
 * Logo Microsoft 365 (4 carrés rouge/vert/jaune/bleu). Pattern "Window" officiel
 * Microsoft, simplifie en grille 2x2 (sans le sub-divider 3x3 plus moderne pour
 * rester lisible a petite taille).
 */
function M365({ size, style, label }: { size: number; style?: CSSProperties; label: string }) {
  // marge interne entre les carres = ~10% de la taille totale
  const cell = size * 0.46;
  const gap  = size * 0.08;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label={label}
      style={{ flexShrink: 0, ...style }}
    >
      {/* Top-left (red/orange) */}
      <rect x="1.5" y="1.5" width="10" height="10" fill="#F25022" />
      {/* Top-right (green) */}
      <rect x="12.5" y="1.5" width="10" height="10" fill="#7FBA00" />
      {/* Bottom-left (blue) */}
      <rect x="1.5" y="12.5" width="10" height="10" fill="#00A4EF" />
      {/* Bottom-right (yellow) */}
      <rect x="12.5" y="12.5" width="10" height="10" fill="#FFB900" />
      {/* (vars cell/gap unused inline - kept for future tweaks) */}
      <desc>{`cell=${cell.toFixed(1)} gap=${gap.toFixed(1)}`}</desc>
    </svg>
  );
}

/**
 * Logo Azure (triangle bleu degrade). Simplifie de la marque officielle en
 * 2 triangles superposes - rendu correct des 16px aux 64px.
 */
function Azure({ size, style, label }: { size: number; style?: CSSProperties; label: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label={label}
      style={{ flexShrink: 0, ...style }}
    >
      <defs>
        {/* Bleu Azure officiel : #0089D6 -> #5EA0EF (degrade) */}
        <linearGradient id="azure-grad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor="#0089D6" />
          <stop offset="100%" stopColor="#5EA0EF" />
        </linearGradient>
      </defs>
      {/* Grand triangle gauche */}
      <path d="M9.5 3 L2 19 L7 19 L13 7.5 Z" fill="url(#azure-grad)" />
      {/* Petit triangle droit (more bleu fonce) */}
      <path d="M11 8.5 L7.5 19 L22 19 L19 14 L14.5 14 L17 9 Z" fill="#0072C6" />
    </svg>
  );
}

const DEFAULT_LABELS: Record<BrandIconProps['name'], string> = {
  m365:  'Microsoft 365',
  azure: 'Microsoft Azure',
};

export function BrandIcon({ name, size = 16, style, label }: BrandIconProps) {
  const finalLabel = label ?? DEFAULT_LABELS[name];
  if (name === 'm365')  return <M365  size={size} style={style} label={finalLabel} />;
  if (name === 'azure') return <Azure size={size} style={style} label={finalLabel} />;
  return null;
}
